import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";

import API_BASE_URL from "../../utils/api";
import ActivityPageCard from "../../components/ActivityPageCard/ActivityPageCard";

/* =========================================================
   CONSTANTS
   ========================================================= */

const SAVED_PAGE_SIZE = 20;

const TAB_NAMES = ["overview", "transcribed", "reviewed", "recent", "saved"];

/* =========================================================
   HELPERS
   ========================================================= */

const formatSummaryTime = (minutes) => {
  const totalMinutes = Math.round(Number(minutes || 0));

  if (totalMinutes < 60) {
    return `${totalMinutes} min`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  return remainingMinutes
    ? `${hours} hr ${remainingMinutes} min`
    : `${hours} hr`;
};

const getInitialTab = (searchParams) => {
  const tabName = searchParams.get("tab");

  const index = TAB_NAMES.indexOf(tabName);

  return index >= 0 ? index : 0;
};

const getPageDate = (page, field) => {
  const value = page?.[field];

  if (!value) {
    return 0;
  }

  const timestamp = new Date(value).getTime();

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const filterAndSortPages = (pages, search, sortOrder, dateField) => {
  const query = search.trim().toLowerCase();

  let result = [...(pages || [])];

  /* SEARCH */

  if (query) {
    result = result.filter((page) => {
      const searchableText = [
        page.title,
        page.documentTitle,
        page.pageNumber,
        page.status,
        page.contentType,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }

  /* SORT */

  result.sort((a, b) => {
    const aDate = getPageDate(a, dateField);
    const bDate = getPageDate(b, dateField);

    return sortOrder === "oldest" ? aDate - bDate : bDate - aDate;
  });

  return result;
};

/* =========================================================
   MY ACTIVITY
   ========================================================= */

const MyActivity = () => {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState(() => getInitialTab(searchParams));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [summary, setSummary] = useState(null);
  const [transcriptions, setTranscriptions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [recent, setRecent] = useState([]);
  const [favorites, setFavorites] = useState([]);

  /* SEARCH / SORT */

  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");

  /* SAVED PAGINATION */

  const [visibleSavedCount, setVisibleSavedCount] = useState(SAVED_PAGE_SIZE);

  /* SNACKBAR */

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const getHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  /* =========================================================
     LOAD DATA
     ========================================================= */

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      const [
        summaryRes,
        transcriptionRes,
        reviewsRes,
        recentRes,
        favoritesRes,
      ] = await Promise.all([
        axios.get(`${API_BASE_URL}/user-activity/summary`, {
          headers: getHeaders(),
        }),

        axios.get(`${API_BASE_URL}/user-activity/transcriptions`, {
          headers: getHeaders(),
        }),

        axios.get(`${API_BASE_URL}/user-activity/reviews`, {
          headers: getHeaders(),
        }),

        axios.get(`${API_BASE_URL}/user-activity/recent?limit=20`, {
          headers: getHeaders(),
        }),

        axios.get(`${API_BASE_URL}/user-activity/favorites`, {
          headers: getHeaders(),
        }),
      ]);

      setSummary(summaryRes.data);

      setTranscriptions(transcriptionRes.data.pages || []);

      setReviews(reviewsRes.data.pages || []);

      setRecent((recentRes.data.pages || []).slice(0, 20));

      setFavorites(favoritesRes.data.pages || []);
    } catch (err) {
      console.error("Failed to load personal activity:", err);

      setError(err.response?.data?.error || "Failed to load your activity.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  /* =========================================================
     KEEP TAB IN URL
     ========================================================= */

  useEffect(() => {
    const tabName = searchParams.get("tab");
    const tabIndex = TAB_NAMES.indexOf(tabName);

    if (tabIndex >= 0 && tabIndex !== tab) {
      setTab(tabIndex);
      setSearch("");
      setSortOrder("newest");
      setVisibleSavedCount(SAVED_PAGE_SIZE);
    }
  }, [searchParams]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);

    setSearch("");
    setSortOrder("newest");
    setVisibleSavedCount(SAVED_PAGE_SIZE);

    if (newValue === 0) {
      setSearchParams({});
    } else {
      setSearchParams({
        tab: TAB_NAMES[newValue],
      });
    }
  };

  const navigateToTab = (newTab) => {
    setTab(newTab);

    setSearch("");
    setSortOrder("newest");
    setVisibleSavedCount(SAVED_PAGE_SIZE);

    if (newTab === 0) {
      setSearchParams({});
    } else {
      setSearchParams({
        tab: TAB_NAMES[newTab],
      });
    }
  };

  /* =========================================================
     SNACKBAR
     ========================================================= */

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  /* =========================================================
     REMOVE FAVORITE
     ========================================================= */

  const removeFavorite = async (page) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/user-activity/favorite/${page.documentId}/${page.pageNumber}`,
        {
          isFavorite: false,
        },
        {
          headers: getHeaders(),
        }
      );

      setFavorites((prev) =>
        prev.filter(
          (item) =>
            !(
              item.documentId === page.documentId &&
              item.pageNumber === page.pageNumber
            )
        )
      );

      showSnackbar("Removed from Saved for Later.", "success");
    } catch (err) {
      console.error("Remove favorite failed:", err);

      showSnackbar("We couldn't remove this page. Please try again.", "error");
    }
  };

  /* =========================================================
     FILTERED DATA
     ========================================================= */

  const filteredTranscriptions = useMemo(
    () => filterAndSortPages(transcriptions, search, sortOrder, "lastWorkedAt"),
    [transcriptions, search, sortOrder]
  );

  const filteredReviews = useMemo(
    () => filterAndSortPages(reviews, search, sortOrder, "reviewedAt"),
    [reviews, search, sortOrder]
  );

  const filteredRecent = useMemo(
    () => filterAndSortPages(recent, search, sortOrder, "lastViewedAt"),
    [recent, search, sortOrder]
  );

  const filteredFavorites = useMemo(
    () => filterAndSortPages(favorites, search, sortOrder, "favoritedAt"),
    [favorites, search, sortOrder]
  );

  const visibleFavorites = filteredFavorites.slice(0, visibleSavedCount);

  const hasMoreFavorites = visibleSavedCount < filteredFavorites.length;

  /* =========================================================
     LOADING
     ========================================================= */

  if (loading) {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{
          minHeight: "60vh",

          display: "flex",
          flexDirection: "column",

          alignItems: "center",
          justifyContent: "center",

          gap: 1.5,
        }}
      >
        <CircularProgress
          size={36}
          sx={{
            color: "#5f5225",
          }}
        />

        <Typography
          variant="body2"
          sx={{
            color: "#5f6368",
          }}
        >
          Loading your activity...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        width: "100%",
        maxWidth: "1500px",
        mx: "auto",

        px: {
          xs: 2,
          sm: 3,
          md: 6,
          lg: 8,
        },

        py: {
          xs: 2,
          sm: 3,
          md: 4,
        },

        overflowX: "hidden",
      }}
    >
      {/* =====================================================
          BACK BUTTON
         ===================================================== */}

      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate("/transcription")}
        aria-label="Back to Transcription"
        sx={{
          mb: {
            xs: 2.5,
            md: 3,
          },

          minHeight: 44,
          px: 1.5,

          color: "#222",

          backgroundColor: "#fff7db",

          border: "1px solid #e6dcae",

          borderRadius: 2,

          textTransform: "none",
          fontWeight: 600,

          "&:hover": {
            backgroundColor: "#f8edc4",
            borderColor: "#cfc28d",
          },

          "&:focus-visible": {
            outline: "3px solid #786d3e",
            outlineOffset: "2px",
            backgroundColor: "#fff7db",
          },
        }}
      >
        Back to Transcription
      </Button>

      {/* =====================================================
          HEADER
         ===================================================== */}

      <Box
        sx={{
          textAlign: "center",

          mb: {
            xs: 3,
            md: 4,
          },
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontWeight: 400,
            color: "#1f2933",
            lineHeight: 1.3,

            fontSize: {
              xs: "1.7rem",
              sm: "1.9rem",
              md: "2rem",
            },
          }}
        >
          My Activity
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: "#5f6368",

            mt: 0.75,
            mx: "auto",

            maxWidth: 720,
            lineHeight: 1.6,

            fontSize: {
              xs: "0.9rem",
              sm: "1rem",
            },
          }}
        >
          View your transcription and review contributions, recently viewed
          pages, and pages saved for later.
        </Typography>
      </Box>

      {/* =====================================================
          ERROR
         ===================================================== */}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
          }}
        >
          {error}
        </Alert>
      )}

      {!error && (
        <>
          {/* =================================================
              SUMMARY CARDS
             ================================================= */}

          <Box
            aria-label="Contribution summary"
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(3, minmax(0, 1fr))",
              },

              gap: {
                xs: 1.25,
                sm: 1.5,
                md: 2,
              },

              mb: {
                xs: 3,
                md: 4,
              },
            }}
          >
            <SummaryCard
              icon={<AccessTimeOutlinedIcon />}
              label="Time contributed"
              value={formatSummaryTime(summary?.totalDurationMinutes)}
            />

            <SummaryCard
              icon={<EditNoteOutlinedIcon />}
              label="Pages transcribed"
              value={summary?.transcribedCount || 0}
            />

            <SummaryCard
              icon={<FactCheckOutlinedIcon />}
              label="Pages reviewed"
              value={summary?.reviewedCount || 0}
            />
          </Box>

          {/* =================================================
              TABS
             ================================================= */}

          <Paper
            elevation={0}
            sx={{
              border: "1px solid #dcdcdc",
              borderRadius: 2.5,

              mb: {
                xs: 3,
                md: 3,
              },

              overflow: "hidden",
            }}
          >
            <Tabs
              value={tab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="Activity categories"
              sx={{
                minHeight: 54,

                "& .MuiTab-root": {
                  minHeight: 54,
                  minWidth: "auto",

                  px: {
                    xs: 1.5,
                    sm: 2,
                  },

                  textTransform: "none",
                  fontWeight: 600,

                  color: "#555",

                  fontSize: {
                    xs: "0.78rem",
                    sm: "0.875rem",
                  },

                  "&:focus-visible": {
                    outline: "3px solid #786d3e",

                    outlineOffset: "-3px",
                  },
                },

                "& .Mui-selected": {
                  color: "#24210f !important",
                  backgroundColor: "#fffaf0",
                },

                "& .MuiTabs-indicator": {
                  height: 3,
                  backgroundColor: "#786d3e",
                },
              }}
            >
              <Tab
                icon={<DashboardOutlinedIcon />}
                iconPosition="start"
                label="Overview"
              />

              <Tab
                icon={<EditNoteOutlinedIcon />}
                iconPosition="start"
                label={`Transcribed (${transcriptions.length})`}
              />

              <Tab
                icon={<FactCheckOutlinedIcon />}
                iconPosition="start"
                label={`Reviewed (${reviews.length})`}
              />

              <Tab
                icon={<HistoryOutlinedIcon />}
                iconPosition="start"
                label={`Recently Viewed (${recent.length})`}
              />

              <Tab
                icon={<BookmarkBorderOutlinedIcon />}
                iconPosition="start"
                label={`Saved (${favorites.length})`}
              />
            </Tabs>
          </Paper>

          {/* =================================================
              OVERVIEW
             ================================================= */}

          {tab === 0 && (
            <Overview
              recent={recent.slice(0, 6)}
              favorites={favorites.slice(0, 6)}
              setTab={navigateToTab}
            />
          )}

          {/* =================================================
              TRANSCRIBED
             ================================================= */}

          {tab === 1 && (
            <>
              <ActivityControls
                search={search}
                setSearch={setSearch}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                placeholder="Search transcribed pages"
              />

              <PageGrid
                pages={filteredTranscriptions}
                emptyText={
                  search
                    ? "No transcribed pages match your search."
                    : "You have not contributed transcription work yet."
                }
                dateLabel="Last worked"
                dateField="lastWorkedAt"
                showTrackedTime
              />
            </>
          )}

          {/* =================================================
              REVIEWED
             ================================================= */}

          {tab === 2 && (
            <>
              <ActivityControls
                search={search}
                setSearch={setSearch}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                placeholder="Search reviewed pages"
              />

              <PageGrid
                pages={filteredReviews}
                emptyText={
                  search
                    ? "No reviewed pages match your search."
                    : "You have not reviewed any pages yet."
                }
                dateLabel="Last reviewed"
                dateField="reviewedAt"
                showTrackedTime
              />
            </>
          )}

          {/* =================================================
              RECENT
             ================================================= */}

          {tab === 3 && (
            <>
              <ActivityControls
                search={search}
                setSearch={setSearch}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                placeholder="Search recently viewed pages"
              />

              <PageGrid
                pages={filteredRecent}
                emptyText={
                  search
                    ? "No recently viewed pages match your search."
                    : "No recently viewed pages yet."
                }
                dateLabel="Last viewed"
                dateField="lastViewedAt"
              />
            </>
          )}

          {/* =================================================
              SAVED
             ================================================= */}

          {tab === 4 && (
            <>
              <ActivityControls
                search={search}
                setSearch={(value) => {
                  setSearch(value);

                  setVisibleSavedCount(SAVED_PAGE_SIZE);
                }}
                sortOrder={sortOrder}
                setSortOrder={(value) => {
                  setSortOrder(value);

                  setVisibleSavedCount(SAVED_PAGE_SIZE);
                }}
                placeholder="Search saved pages"
              />

              <PageGrid
                pages={visibleFavorites}
                emptyText={
                  search
                    ? "No saved pages match your search."
                    : "You have not saved any pages for later."
                }
                dateLabel="Saved"
                dateField="favoritedAt"
                showRemove
                onRemove={removeFavorite}
              />

              {hasMoreFavorites && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 3,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() =>
                      setVisibleSavedCount((prev) => prev + SAVED_PAGE_SIZE)
                    }
                    sx={{
                      minHeight: 44,
                      px: 3,

                      color: "#3f3a20",

                      borderColor: "#cfc28d",

                      backgroundColor: "#fff",

                      textTransform: "none",
                      fontWeight: 600,

                      borderRadius: 2,

                      "&:hover": {
                        borderColor: "#a99e68",
                        backgroundColor: "#fff7db",
                      },

                      "&:focus-visible": {
                        outline: "3px solid #786d3e",

                        outlineOffset: "2px",
                      },
                    }}
                  >
                    Load More
                  </Button>
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* =====================================================
          SNACKBAR
         ===================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={closeSnackbar}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/* =========================================================
   ACTIVITY CONTROLS
   ========================================================= */

const ActivityControls = ({
  search,
  setSearch,
  sortOrder,
  setSortOrder,
  placeholder,
}) => (
  <Box
    sx={{
      display: "flex",

      flexDirection: {
        xs: "column",
        sm: "row",
      },

      alignItems: {
        xs: "stretch",
        sm: "center",
      },

      justifyContent: "space-between",

      gap: 1.5,

      mb: 2.5,
    }}
  >
    {/* SEARCH */}

    <TextField
      value={search}
      onChange={(event) => setSearch(event.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      size="small"
      sx={{
        width: {
          xs: "100%",
          sm: 360,
        },

        "& .MuiOutlinedInput-root": {
          minHeight: 44,
          borderRadius: 2,

          backgroundColor: "#fff",

          "&:hover fieldset": {
            borderColor: "#a99e68",
          },

          "&.Mui-focused fieldset": {
            borderColor: "#786d3e",
            borderWidth: "2px",
          },
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchRoundedIcon
              aria-hidden="true"
              sx={{
                color: "#626262",
              }}
            />
          </InputAdornment>
        ),
      }}
    />

    {/* SORT */}

    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <SortRoundedIcon
        aria-hidden="true"
        sx={{
          color: "#626262",
          display: {
            xs: "none",
            sm: "block",
          },
        }}
      />

      <FormControl
        size="small"
        sx={{
          minWidth: {
            xs: "100%",
            sm: 155,
          },
        }}
      >
        <Select
          value={sortOrder}
          onChange={(event) => setSortOrder(event.target.value)}
          inputProps={{
            "aria-label": "Sort activity",
          }}
          sx={{
            minHeight: 44,
            borderRadius: 2,

            backgroundColor: "#fff",

            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#a99e68",
            },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#786d3e",
            },
          }}
        >
          <MenuItem value="newest">Newest first</MenuItem>

          <MenuItem value="oldest">Oldest first</MenuItem>
        </Select>
      </FormControl>
    </Box>
  </Box>
);

/* =========================================================
   SUMMARY CARD
   ========================================================= */

const SummaryCard = ({ icon, label, value }) => (
  <Paper
    elevation={0}
    sx={{
      p: {
        xs: 1.75,
        sm: 2,
        md: 2.25,
      },

      minHeight: {
        xs: 100,
        sm: 112,
      },

      height: "100%",

      border: "1px solid #dcdcdc",
      borderRadius: 2.5,

      display: "flex",
      alignItems: "center",

      gap: {
        xs: 1.5,
        md: 2,
      },

      transition: "border-color 160ms ease, box-shadow 160ms ease",

      "&:hover": {
        borderColor: "#bdb58f",

        boxShadow: "0 3px 12px rgba(0,0,0,0.05)",
      },

      "@media (prefers-reduced-motion: reduce)": {
        transition: "none",
      },
    }}
  >
    <Box
      aria-hidden="true"
      sx={{
        width: {
          xs: 42,
          md: 48,
        },

        height: {
          xs: 42,
          md: 48,
        },

        flexShrink: 0,

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        borderRadius: 2,

        backgroundColor: "#fff7db",
        color: "#4a452f",

        "& svg": {
          fontSize: {
            xs: 22,
            md: 25,
          },
        },
      }}
    >
      {icon}
    </Box>

    <Box
      sx={{
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: {
            xs: "1.25rem",
            md: "1.45rem",
          },

          lineHeight: 1.25,

          fontWeight: 700,
          color: "#1f2933",
        }}
      >
        {value}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          mt: 0.4,

          color: "#555",
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
    </Box>
  </Paper>
);

/* =========================================================
   OVERVIEW
   ========================================================= */

const Overview = ({ recent, favorites, setTab }) => (
  <Box>
    {/* RECENTLY VIEWED */}

    <OverviewSectionHeader
      title="Recently Viewed"
      icon={<HistoryOutlinedIcon />}
      action="View all"
      onClick={() => setTab(3)}
    />

    <PageGrid
      pages={recent}
      emptyText="No recently viewed pages yet."
      dateLabel="Last viewed"
      dateField="lastViewedAt"
    />

    {/* SAVED FOR LATER */}

    <Box
      sx={{
        mt: {
          xs: 4,
          md: 5,
        },
      }}
    >
      <OverviewSectionHeader
        title="Saved for Later"
        icon={<BookmarkBorderOutlinedIcon />}
        action="View all"
        onClick={() => setTab(4)}
      />

      <PageGrid
        pages={favorites}
        emptyText="No saved pages yet."
        dateLabel="Saved"
        dateField="favoritedAt"
      />
    </Box>
  </Box>
);

/* =========================================================
   OVERVIEW SECTION HEADER
   ========================================================= */

const OverviewSectionHeader = ({ title, icon, action, onClick }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",

      gap: 2,
      mb: 2,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",

        gap: 1,
        minWidth: 0,
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          display: "flex",
          alignItems: "center",

          color: "#4a452f",

          flexShrink: 0,

          "& svg": {
            fontSize: 21,
          },
        }}
      >
        {icon}
      </Box>

      <Typography
        component="h2"
        variant="h6"
        sx={{
          color: "#1f2933",
          fontWeight: 600,
          lineHeight: 1.3,

          fontSize: {
            xs: "1rem",
            sm: "1.15rem",
          },
        }}
      >
        {title}
      </Typography>
    </Box>

    <Button
      onClick={onClick}
      endIcon={<ArrowForwardRoundedIcon />}
      sx={{
        flexShrink: 0,

        minHeight: 44,

        px: {
          xs: 1,
          sm: 1.5,
        },

        color: "#3f3a20",

        textTransform: "none",
        fontWeight: 600,

        borderRadius: 2,

        "&:hover": {
          backgroundColor: "#fff7db",
        },

        "&:focus-visible": {
          outline: "3px solid #786d3e",
          outlineOffset: "2px",
        },
      }}
    >
      {action}
    </Button>
  </Box>
);

/* =========================================================
   PAGE GRID
   ========================================================= */

const PageGrid = ({
  pages,
  emptyText,
  dateLabel,
  dateField,
  showTrackedTime = false,
  showRemove = false,
  onRemove,
}) => {
  if (!pages?.length) {
    return (
      <Paper
        elevation={0}
        role="status"
        sx={{
          px: 2,

          py: {
            xs: 3,
            sm: 3.5,
            md: 4,
          },

          textAlign: "center",

          border: "1px dashed #bdbdbd",

          borderRadius: 2.5,

          backgroundColor: "#fff",
        }}
      >
        <DescriptionOutlinedIcon
          aria-hidden="true"
          sx={{
            fontSize: 32,
            color: "#686868",
            mb: 0.75,
          }}
        />

        <Typography
          variant="body2"
          sx={{
            color: "#555",
          }}
        >
          {emptyText}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",

        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",

          sm: "repeat(2, minmax(0, 1fr))",

          lg: "repeat(3, minmax(0, 1fr))",
        },

        gap: {
          xs: 1.5,
          sm: 2,
          lg: 2.25,
        },

        width: "100%",
        minWidth: 0,
      }}
    >
      {pages.map((page) => (
        <ActivityPageCard
          key={`${page.documentId}-${page.pageNumber}`}
          page={page}
          dateLabel={dateLabel}
          dateValue={page[dateField]}
          showTrackedTime={showTrackedTime}
          showRemove={showRemove}
          onRemove={onRemove}
        />
      ))}
    </Box>
  );
};

export default MyActivity;
